import { useEffect, useState } from 'react';
import { Briefcase, CalendarBlank, User } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { EntityType } from '@ruta/shared';
import { useAssistantMentions } from '@/hooks/useAssistantMentions';
import type { MentionOption, MentionTab } from '@/lib/api/assistantMentions';

const TABS: Array<{ id: MentionTab; label: string }> = [
  { id: 'services', label: 'Services' },
  { id: 'visits', label: 'Visits' },
  { id: 'customers', label: 'Customers' },
];

function rowIcon(entityType: EntityType) {
  const props = { className: 'h-4 w-4 text-white/45 shrink-0' };
  if (entityType === EntityType.VISIT) return <CalendarBlank {...props} />;
  if (entityType === EntityType.CUSTOMER) return <User {...props} />;
  return <Briefcase {...props} />;
}

// `query` is the live text typed after "@" in the composer; the picker filters
// the active tab by it as you type. Tabs stay switchable.
export function AssistantMentionPicker({
  query,
  onSelect,
  className,
}: {
  query: string;
  onSelect: (option: MentionOption) => void;
  className?: string;
}) {
  const [tab, setTab] = useState<MentionTab>('services');
  const [debounced, setDebounced] = useState(query);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 150);
    return () => clearTimeout(t);
  }, [query]);

  const { data: options = [], isFetching } = useAssistantMentions(tab, debounced, true);

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-xl border border-white/[0.1] bg-[rgba(16,32,16,0.98)] shadow-[0_16px_40px_rgba(0,0,0,0.5)]',
        className,
      )}
    >
      <div className="flex items-center gap-1 px-2 pt-2 pb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-2.5 py-1 rounded-md text-[11.5px] font-medium transition-colors',
              tab === t.id
                ? 'bg-white/[0.12] text-white'
                : 'text-white/50 hover:text-white/80 hover:bg-white/[0.06]',
            )}
          >
            {t.label}
          </button>
        ))}
        {query && (
          <span className="ml-auto pr-1 text-[10.5px] text-white/40 truncate max-w-[120px]">
            “{query}”
          </span>
        )}
      </div>

      <div className="max-h-56 overflow-y-auto p-1.5 border-t border-white/[0.06]">
        {options.length === 0 ? (
          <div className="px-2 py-6 text-center text-[11.5px] text-white/35">
            {isFetching ? 'Searching…' : 'No matches'}
          </div>
        ) : (
          options.map((o) => (
            <button
              key={`${o.entityType}:${o.entityId}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelect(o)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-white/[0.07] transition-colors"
            >
              {rowIcon(o.entityType)}
              <span className="flex-1 min-w-0">
                <span className="block text-[12.5px] text-white/85 truncate leading-tight">
                  {o.displayName}
                </span>
                {o.subtitle && (
                  <span className="block text-[10.5px] text-white/40 truncate leading-tight">
                    {o.subtitle}
                  </span>
                )}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
